import { BitkitButton, BitkitTooltip, IconCross, IconHashtag, IconReplace, useResponsive } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { LinkOverlay } from '@chakra-ui/react/link';
import { Skeleton } from '@chakra-ui/react/skeleton';
import { MouseEventHandler, useRef, useState } from 'react';

import AppAvatar from '@/components/AppAvatar/AppAvatar';
import AppTitle from '@/components/AppTitle/AppTitle';
import BuildMetaData from '@/components/BuildListItem/BuildMetaData/BuildMetaData';
import { Build, getBuildStatusColors, getBuildStatusIconName, getBuildStatusText } from '@/models/Build';
import { getGitProviderCommitHashUrl, getGitProviderPullRequestUrl } from '@/models/GitProvider';
import AppForAppPage from '@/pages/App/types/AppForAppPage';
import { basePathForApp, buildDetailsPagePath, pipelineDetailsPagePath } from '@/utils/monolithApiRouteService';

import InstallableArtifacts from './InstallableArtifacts/InstallableArtifacts';
import PullRequestOrCommitHashLink from './PullRequestOrCommitHashLink/PullRequestOrCommitHashLink';

type BuildStatusIconProps = {
  build: Build;
};
const BuildStatusIcon = ({ build, ...rest }: BuildStatusIconProps) => {
  const statusName = getBuildStatusText(build);
  const StatusIcon = getBuildStatusIconName(build);

  return (
    <BitkitTooltip text={statusName}>
      {/* StatusIcon is one of a fixed set of module-level icon components, selected by status — not created during render. */}
      {/* eslint-disable-next-line react-hooks/static-components */}
      <StatusIcon size="24" color={getBuildStatusColors(build).iconColor} zIndex={1} {...rest} />
    </BitkitTooltip>
  );
};

type BuildCommitMessageAndPrIdOrCommitHashProps = {
  build: Build;
  app?: AppForAppPage;
};
const BuildCommitMessageAndPrIdOrCommitHash = ({ build, app }: BuildCommitMessageAndPrIdOrCommitHashProps) => {
  const appSlug = build.repository?.slug || app?.slug;
  const gitProvider = build.repository?.provider || app?.provider;
  const gitOwner = build.repository?.repoOwner || app?.gitOwner;
  const gitRepoSlug = build.repository?.repoSlug || app?.gitRepoSlug;
  const commitHashUrl = getGitProviderCommitHashUrl(
    gitProvider,
    gitOwner,
    gitRepoSlug,
    build.commitHash,
    build.pullRequestId,
  );

  const pullRequestUrl = getGitProviderPullRequestUrl(gitProvider, gitOwner, gitRepoSlug, build.pullRequestId);

  let url: string;
  if (build?.isPipeline) {
    url = pipelineDetailsPagePath(appSlug, build.slug);
  } else {
    url = buildDetailsPagePath(appSlug, build.slug);
  }

  return (
    <Box
      display="flex"
      flexDirection={['column', 'row']}
      gap="4"
      justifyContent="flex-start"
      alignItems="center"
      overflow="hidden"
    >
      <LinkOverlay
        flexShrink={1}
        minWidth="0"
        textStyle="body/md/regular"
        href={url}
        maxWidth="720px"
        textOverflow="ellipsis"
        overflow="hidden"
        whiteSpace="nowrap"
      >
        {build.commitMessage || 'No commit message'}
      </LinkOverlay>
      <PullRequestOrCommitHashLink
        size="2"
        gitProvider={gitProvider}
        commitHash={build.commitHash}
        commitHashUrl={commitHashUrl}
        pullRequestId={build.pullRequestId}
        pullRequestUrl={pullRequestUrl}
      />
      <InstallableArtifacts artifacts={build.artifacts} paddingInlineStart={[0, '4']} />
    </Box>
  );
};

type BuildActionProps = {
  build: Build;
  onRebuildClick: (baseBuild: Build) => void;
  onAbortClick: (baseBuild: Build) => void;
};
const BuildAction = ({ build, onRebuildClick, onAbortClick }: BuildActionProps) => {
  const { isMobile } = useResponsive();
  const [isActionLoading, setActionLoading] = useState(false);

  const isNotRebuildable: boolean = Boolean(build.finishedAt && !build.rebuildable);
  const actionText = build.finishedAt ? 'Rebuild' : 'Abort';
  const handleBuildAction: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();
    if (build.finishedAt) {
      setActionLoading(true);
    }
    return build.finishedAt ? onRebuildClick(build) : onAbortClick(build);
  };

  return (
    <BitkitButton
      disabledReason="Not rebuildable"
      variant={build.finishedAt ? 'tertiary' : 'danger-tertiary'}
      size={isMobile ? 'lg' : 'sm'}
      icon={build.finishedAt ? IconReplace : IconCross}
      onClick={handleBuildAction}
      marginInlineEnd="12"
      state={isActionLoading ? 'loading' : isNotRebuildable ? 'disabled' : undefined}
      zIndex={1}
      _hover={
        isActionLoading ? undefined : { background: build.finishedAt ? 'color/purple/moderate' : 'color/red/moderate' }
      }
    >
      {actionText}
    </BitkitButton>
  );
};

type BuildListItemUpperSectionProps = {
  build: Build;
  app?: AppForAppPage;
  showAppInfo: boolean;
  canRunBuild: boolean;
  onRebuildClick?: (baseBuild: Build) => void;
  onAbortClick?: (baseBuild: Build) => void;
};
const BuildItemUpperSection = ({
  build,
  app,
  showAppInfo,
  canRunBuild,
  onRebuildClick,
  onAbortClick,
}: BuildListItemUpperSectionProps) => {
  const { isMobile } = useResponsive();
  const ref = useRef<HTMLDivElement>(null);
  const shouldShowBuildAction = canRunBuild && onRebuildClick && onAbortClick;

  return (
    <>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        borderBottom="1px solid"
        borderBottomColor="border/minimal"
        _groupHover={{ borderColor: 'border/regular' }}
      >
        <Box display="flex" padding="12" gap="12" alignItems="center" overflow="hidden" width="100%">
          <BuildStatusIcon build={build} />
          {showAppInfo ? (
            <>
              {build?.repository ? (
                <AppAvatar
                  name={build.repository.title}
                  src={build.repository.avatarUrl}
                  projectType={build.repository.projectType}
                  size="40"
                />
              ) : (
                <Skeleton height="40" width="40" borderRadius="8" />
              )}
              <Box display="flex" flexDirection="column" flexGrow={1} minWidth="0">
                {build?.repository ? (
                  <AppTitle
                    app={build.repository}
                    href={basePathForApp(build.repository.slug)}
                    alignSelf="flex-start"
                    lineHeight="24px"
                  />
                ) : (
                  <Skeleton height="20" width="128" />
                )}
                {isMobile ? (
                  <BitkitTooltip text="Build number">
                    <BuildMetaData icon={IconHashtag} gap="0" minWidth="0" ref={ref} zIndex={1}>
                      {build.buildNumber}
                    </BuildMetaData>
                  </BitkitTooltip>
                ) : (
                  <BuildCommitMessageAndPrIdOrCommitHash build={build} app={app} />
                )}
              </Box>
            </>
          ) : (
            <BuildCommitMessageAndPrIdOrCommitHash build={build} app={app} />
          )}
        </Box>
        {shouldShowBuildAction && (
          <BuildAction build={build} onRebuildClick={onRebuildClick} onAbortClick={onAbortClick} />
        )}
      </Box>
      {showAppInfo && isMobile ? (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          gap="4"
          padding="12"
          borderTop="1px solid"
          borderTopColor="border/minimal"
          flexGrow={1}
        >
          <BuildCommitMessageAndPrIdOrCommitHash build={build} app={app} />
        </Box>
      ) : null}
    </>
  );
};

export default BuildItemUpperSection;
